import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { AppSettingService } from '../app.service';
import { UserInfoService } from '../user/user-info.service';

@Injectable({
	providedIn: 'root'
})
export class MyJourneyAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient
	) { }
	
	private url = AppSettingService.appConfig.JourneyAPIUrl + "MyJourney";

	search(startPoint: string, endPoint: string, passthroughId: string, mapOptionId: string = 'default'): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/Search/' + mapOptionId + '/' + startPoint + "/" + endPoint + "/" + passthroughId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	passthrough(startPoint: string, endPoint: string, mapOptionId: string = 'default'): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/Passthrough/' + mapOptionId + '/' + startPoint + "/" + endPoint,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	save(journeyId: string, journeyName: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/User/Save/' + journeyId + '?name=' + encodeURIComponent(journeyName),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	editPrimaryJourney(journeyData: any): Rx.Observable<any> {
		//if edit journey same journeyUserId as persist journey id, then clear cache
		return this.http.post<any>(
			this.url + '/User/EditPrimary',
			{
				"journeyId": journeyData.journeyId,
				"journeyName": journeyData.journeyName,
				"journeyWaypoints": journeyData.journeyWaypoints
			},
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	editLocalJourney(journeyData: any): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + '/User/EditSub',
			{
				"journeyUserId": journeyData.journeyUserId,
				"journeyWaypoints": journeyData.journeyWaypoints,
				"parentJourneyLocalId": journeyData.parentJourneyLocalId
			},
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	remove(journeyUserId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/User/Remove/' + journeyUserId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	list(userId:string, offset?:number, limit?:number) : Rx.Observable<any> {
		if (!offset) {
			offset = 0;
		}
		if (!limit) {
			limit = 500;
		}
		userId = (userId??"").trim();
		return this.http.get<any>(
			this.url + "/User?offset=" + offset + "&limit=" + limit + (userId != "" ? "&UserId=" + userId : ""),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getUserJourneyDetail(journeyUserId: string, userId: string, mapObjectId: string): Rx.Observable<any> {
		userId = userId??"";
		mapObjectId = mapObjectId ?? "";

		return this.http.get<any>(
			this.url + "/User/" + journeyUserId + "?UserId=" + encodeURIComponent(userId) + "&mapObjectId=" + encodeURIComponent(mapObjectId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getJourneyDetail(journeyId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/Journey/' + journeyId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	toggleUserJourneyPersist(journeyUserId: string, persist: boolean, showPersist:boolean): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/User/Persist/' + journeyUserId + '/' + persist.toString() + (showPersist == null ? "" : "?show=" + showPersist.toString()),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getUserJourneyPersist(mapObjectId:string): Rx.Observable<any> {
		mapObjectId = mapObjectId == null ? "" : mapObjectId;
		return this.http.get<any>(
			this.url + "/User/Persist?mapObjectId=" + mapObjectId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(
			map(
				response => {
					return response.Detail;
				}
			)
		);
	}


	getUserSubjourney (journeyId:string, mapVersion:string, mapObjectId:string, journeyLocalId:string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/User/Subjourney/' + journeyId + '/' + mapVersion + '/' + mapObjectId + '?journeyLocalId=' + journeyLocalId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	
	getTempJourneyWaypoints(journeyId:string, mapObjectId:string, journeyLocalId:string): Rx.Observable<any> {
		mapObjectId = mapObjectId == null ? "" : mapObjectId;
		return this.http.get<any>(
			this.url + '/Search/' + journeyId + "?mapObjectId=" + mapObjectId + '&journeyLocalId=' + journeyLocalId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	searchStartPoint(mapversion: string, endPointId:string, keyword:string, offset:number, limit:number): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/StartPoint/' + encodeURIComponent(endPointId) + '/' + mapversion + '?keyword=' + encodeURIComponent(keyword) + '&limit=' + limit.toString() + '&offset=' + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getValidStartPoint(invalidStartPointMapObjectId:string , limit:number, offset:number): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/GetValidStartPoint?invalidStartPoint=' + invalidStartPointMapObjectId + '&limit=' + limit.toString() + '&offset=' + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}
